/**
 * @file Various utilies for handling prices and currency.
 */

/**
 * Given a price in cents it formats it so that it can be
 * accepted by input[type="number"] components which are
 * state controlled.
 *
 * Used mainly to format integer price reponses in cents
 * from the server and display them in a component.
 *
 * E.g.
 * For price === 0, return is '0.00'
 * for price === 11204, return is '112.04'
 * for falsey non-zero prices, return is ''.
 *
 * @param {number} price - Price in cents.
 * @param {boolean} [trimDecimalZeros] - Whether to trim decimal zeros or not (i.e. 52.00 vs 52).
 * @param {number} [fractionDigits] - The amount of decimal digits to keep (default 2)
 * @returns {string} - Either an empty string if price is falsy
 * (zero included) or the price string with 2 decimals.
 */
export const formatPriceForDisplay = (
  price,
  trimDecimalZeros,
  fractionDigits = 2,
) => {
  if (price === 0) {
    return trimDecimalZeros ? '0' : '0.00';
  }

  if (!price) {
    return '';
  }

  const floored = price / 100;
  const isWhole = !(floored % 1);

  return isWhole && trimDecimalZeros
    ? floored.toString()
    : floored.toFixed(fractionDigits);
};

/**
 * Given a price string formatted usually by `formatPriceForDisplay`
 * returns the same price in number type and multiplied by 100 (in cents).
 *
 * Used to properly format price input for sending to the server.
 *
 * @param {string} price - Price string.
 * @returns {number} - Initial price parsed and multiplied by 100 (supposed to return cents).
 */
export const formatPriceForUpdate = price => parseFloat(price) * 100;

/**
 * Formats a number to thousands accepting an optional decimal symbol
 * https://stackoverflow.com/a/2901298/6267661.
 *
 * @param {string|number} num The string or number to format.
 * @param {string} [decimal] The decimal symbol.
 * @returns {string}
 */
export const formatNumber = (num, decimal = ',') =>
  num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, decimal);
